import type { IAccount } from "@/modules/account/common/types";
import { useGetAccountTransferTemplateQuery } from "@/modules/template/services";
import { accountCommissionFieldKeys } from "@/modules/transaction/common/constants";
import { TransactionTypes, TransferKeys } from "@/modules/transaction/common/enum";
import type {
  IAccountTransferForm,
  IAccountTransferRequest
} from "@/modules/transaction/common/types";
import { SaveAsTemplate } from "@/modules/transaction/components/SaveAsTemplate";
import { getBeneficiaryLegalStatusOptions } from "@/modules/transaction/helpers";
import { useAccountTransfer } from "@/modules/transaction/hooks";
import {
  useCreateAccountTransferMutation,
  useGetAccountTransferQuery,
  useGetUrgentAvailabilityQuery,
  useLazyGetAccountTransferCommissionQuery,
  useUpdateAccountTransferMutation,
} from "@/modules/transaction/services";
import { CURRENCY, DATE_FORMAT, INPUT_MAX_LIMIT, } from "@/shared/common/constants";
import { BeneficiaryType, SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import {
  OwnAccountSelectOption,
  Select,
  TransferBeneficiaries,
  TransferLayout
} from "@/shared/components";
import {
  clampToCurrentDate,
  cleanupAmount,
  formatDate,
  isAmeriaAccount,
  isValidAccount,
  isValidAmount,
  mergeByKeys,
  omitKey
} from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { SimpleDatePicker } from "@ab.uitools/ui-library/components/DatePicker";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { Switcher } from "@ab.uitools/ui-library/components/Switcher";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  accountsList: IAccount[];
  isEdit?: boolean;
  isCopy?: boolean;
  setIsAccountNumberInvalid: Dispatch<SetStateAction<boolean>>;
  isNameUnique: boolean;
  setIsNameUnique: Dispatch<SetStateAction<boolean>>;
}

type TAccountTransferRequestData = IAccountTransferRequest & {
  templateName?: string,
  isSaveAsTemplate: boolean
};

export const AccountTransferForm = ({
  accountsList,
  isEdit,
  isCopy,
  isNameUnique,
  setIsNameUnique,
  setIsAccountNumberInvalid,
}: Props) => {
  const { t, i18n: { language } } = useTranslation();
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const idempotencyKey = useRef(uuid4()).current;

  const templateId = searchParams.get(SearchParamKeys.TemplateId);
  const prefilledAccount = searchParams.get(SearchParamKeys.BeneficiaryNumber);

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});
  const [beneficiaryName, setBeneficiaryName] = useState<string>("");

  const {
    reset,
    values,
    errors,
    setValue,
    accountData,
    fieldChanges,
    canGetCommission,
    handleClickTarget,
    accountsFilteredList,
    handleChangeBeneficiaryAccount,
  } = useAccountTransfer({
    accountsList,
    beneficiaryName,
    commissionFieldKeys: accountCommissionFieldKeys,
    setIsAccountNumberInvalid,
  });

  const [getCommission] = useLazyGetAccountTransferCommissionQuery();
  const {
    data: template,
    isLoading: isGetTemplateLoading,
  } = useGetAccountTransferTemplateQuery({
    id: templateId as string,
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !templateId
  });

  const {
    data: accountTransfer,
    isLoading: isGetTransferLoading,
  } = useGetAccountTransferQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const { data: isUrgentAvailable = false } = useGetUrgentAvailabilityQuery({});
  const [createAccountTransferApi, { isLoading: isCreateTransferLoading }] = useCreateAccountTransferMutation();
  const [updateAccountTransferApi, { isLoading: isUpdateTransferLoading }] = useUpdateAccountTransferMutation();

  const handleChangeUrgent = (event: boolean) => {
    if (event) {
      setValue(TransferKeys.ScheduleDate, new Date());
    }
  };

  const accountTransferAction = useCallback((values: IAccountTransferForm) => {
    const decimals = values.currency === CURRENCY.AMD.label ? 1 : 2;
    const data = {
      ...values,
      amount: +(+cleanupAmount(`${values.amount}`)).toFixed(decimals),
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount),
      commissionAccount: accountsList?.find(({ number }) => number === values.commissionAccount),
    };
    const requestData: TAccountTransferRequestData = omitKey({
      ...data,
      scheduleDate: formatDate(values.scheduleDate, DATE_FORMAT.YYYY_MM_DD),
    }, [TransferKeys.TemplateName, TransferKeys.IsSaveAsTemplate]);

    return isEdit ?
      updateAccountTransferApi({
        data: requestData,
        id: `${accountTransfer?.id}`
      }).unwrap() :
      createAccountTransferApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, accountTransfer]);

  const setDefaultValues = () => {
    const data = {
      creditAccount: accountTransfer?.creditAccount,
      debitAccount: accountTransfer?.debitAccount.number,
      transactionNumber: isEdit ?
        accountTransfer?.transactionNumber : values.transactionNumber,
      beneficiary: accountTransfer?.beneficiary,
      scheduleDate: clampToCurrentDate(accountTransfer?.scheduleDate),
      amount: accountTransfer?.amount,
      tin: accountTransfer?.tin,
      fee: accountTransfer?.commissionAmount || 0,
      purpose: accountTransfer?.purpose,
      isUrgent: isUrgentAvailable && !isCopy ? accountTransfer?.isUrgent : false,
      currency: accountTransfer?.currency,
      beneficiaryLegalStatus: accountTransfer?.beneficiaryLegalStatus,
      commissionAccount: accountTransfer?.commissionAccount?.number,
    };
    reset?.(data);
    setDefaultData({
      ...data,
      scheduleDate: new Date(accountTransfer?.scheduleDate as string),
    });
  };

  const currentData = useMemo(() => ({
    creditAccount: values.creditAccount,
    debitAccount: values.debitAccount,
    transactionNumber: values.transactionNumber,
    beneficiary: isAmeriaAccount(values.creditAccount) ? accountTransfer?.beneficiary : values.beneficiary,
    scheduleDate: new Date(values.scheduleDate),
    amount: +cleanupAmount(values.amount),
    tin: values.tin,
    fee: values.fee,
    purpose: values.purpose,
    isUrgent: values.isUrgent,
    currency: values.currency,
    commissionAccount: values.commissionAccount,
    beneficiaryLegalStatus: values.beneficiaryLegalStatus,
  }), [values, accountTransfer]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  const isUrgent = useMemo(() => {
    return isUrgentAvailable &&
      !isAmeriaAccount(values.creditAccount) &&
      values.creditAccount &&
      values.debitAccount;
  }, [
    isUrgentAvailable,
    values.creditAccount,
    values.debitAccount,
  ]);

  const maxAvailableDate = useMemo(() => {
    return values.isUrgent ? new Date() : undefined;
  }, [values.isUrgent]);

  useEffect(() => {
    if ((isEdit || isCopy) && !!accountTransfer?.id) {
      handleChangeBeneficiaryAccount(accountTransfer.creditAccount, {
        beneficiary: accountTransfer?.beneficiary,
        onAmeriaAccount: () => setValue(TransferKeys.IsUrgent, false),
      });
      setDefaultValues();
    }
  }, [accountTransfer]);

  useEffect(() => {
    if (template) {
      const data = mergeByKeys(values, {
        ...template,
        currency: template?.debitAccount?.currency,
        commissionAccount: template?.commissionAccount?.number,
        debitAccount: template?.debitAccount?.number,
      });
      reset?.(data);
      handleChangeBeneficiaryAccount(template.creditAccount, {
        beneficiary: template?.beneficiary,
        onAmeriaAccount: () => setValue(TransferKeys.IsUrgent, false),
      });
    }
  }, [template]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.ScheduleDate, new Date());
    }
    if (!isEdit && !isCopy && !templateId) {
      setValue(TransferKeys.Purpose, t("transfer.account.purpose"));
    }
    if (!isEdit && !isCopy && prefilledAccount) {
      handleChangeBeneficiaryAccount(prefilledAccount);
    }
  }, []);

  return (
    <TransferLayout
      title={t("transfer.account.title")}
      page="Account transfer"
      transactionType={TransactionTypes.AccountTransfer}
      getCommission={getCommission}
      accountsList={accountsList}
      isLoading={isCreateTransferLoading || isUpdateTransferLoading}
      isGetTransferLoading={isGetTransferLoading || isGetTemplateLoading}
      isSaveDisabled={!isNameUnique && values.isSaveAsTemplate}
      isSignDisabled={!isNameUnique && values.isSaveAsTemplate}
      isEdit={isEdit}
      hasChanges={hasChanges}
      canGetCommission={canGetCommission}
      fieldChanges={fieldChanges}
      getTransferId={accountTransferAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <TransferBeneficiaries
                name="creditAccount"
                entryType={BeneficiaryType.Account}
                label={t("transfer.receiverAccount")}
                onChange={(v) => handleChangeBeneficiaryAccount(v)}
                placeholder={t("transfer.typeOrChooseAnAccount")}
                setBeneficiaryName={setBeneficiaryName}
              />
              {accountData?.bankName && (
                <Text className="mt-8" type="tertiary">
                  {accountData?.bankName}
                </Text>
              )}
              {isValidAccount(values.creditAccount) &&
                !isAmeriaAccount(values.creditAccount) && (
                  <FormField
                    name="beneficiaryLegalStatus"
                    className="mb-32 mt-32"
                    dataId="beneficiaryLegalStatus"
                    shouldValidate
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Select
                        {...props}
                        required
                        options={getBeneficiaryLegalStatusOptions({ t })}
                        label={t("transfer.beneficiaryLegalStatus")}
                        placeholder={t("transfer.chooseBeneficiaryLegalStatus")}
                      />
                    )}
                  />
                )}
              {accountData?.holder && isAmeriaAccount(values.creditAccount) ? (
                <div className="mt-32 flexbox justify-content--between">
                  <Text className="mr-16">{t("transfer.accountHolder")}</Text>
                  <Text className="text-align--end">{accountData?.holder}</Text>
                </div>
              ) : (
                <FormField
                  name="beneficiary"
                  className="mb-8 mt-32"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      required
                      isTrimValues
                      label={t("transfer.beneficiaryName")}
                      placeholder={t("transfer.enterBeneficiaryName")}
                    />
                  )}
                />
              )}
            </>,
            title: t("general.beneficiaryDetails"),
            value: "beneficiary_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="debitAccount"
                className="mb-32"
                dataId="debitAccount"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    className="custom-select"
                    options={accountsFilteredList || []}
                    label={t("transfer.debitAccount")}
                    placeholder={t("transfer.chooseDebitAccount")}
                    handleChange={(val) => handleClickTarget(val as SingleValue<TOption>)}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="amount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    label={t("general.amount")}
                    placeholder={t("general.enterAmount")}
                    thousandSeparator=","
                    decimalScale={values.currency === CURRENCY.AMD.label ? 1 : 2}
                    rightIconProps={{
                      Component: CURRENCY[values.currency]?.symbolIcon || "",
                    }}
                    isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-32"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <div className="flexbox justify-content--between">
                <div className="flex-direction--column">
                  <Text type="primary" weight="bolder">
                    {t("transfer.urgentTransfer")}
                  </Text>
                  <Text type="secondary">
                    {t("transfer.urgentTransferDescription")}
                  </Text>
                </div>
                <FormField
                  name="isUrgent"
                  className="mb-8"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Switcher
                      {...props}
                      disabled={!isUrgent}
                      onClick={handleChangeUrgent}/>
                  )}
                />
              </div>
              <Divider
                color="light"
                type="primary"
                className="my-16"
                isHorizontal
              />
              <SaveAsTemplate
                setValue={setValue}
                setIsNameUnique={setIsNameUnique}
                isSaveAsTemplate={values.isSaveAsTemplate}
              />
            </>,
            title: t("transfer.transferDetails"),
            value: "transfer_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="transactionNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("transfer.transactionNumber")}
                    placeholder={t("transfer.enterTransactionNumber")}
                  />
                )}/>
              <FormField
                name="scheduleDate"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <SimpleDatePicker
                    {...props}
                    required
                    closeOnScroll
                    dataId="scheduleDate"
                    locale={language}
                    format={DATE_FORMAT.DD_MM_YYYY}
                    label={t("general.date")}
                    value={props.value as Date}
                    minDate={new Date()}
                    maxDate={maxAvailableDate}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </TransferLayout>
  );
};
